/**
 * Układ katalogów audio Radia Adamowo - jedyne miejsce z nazwami folderów.
 *
 * Korzysta z niego zarówno plugin builda (`radioCatalog.vite.ts`), jak i
 * aplikacja, więc nazwy katalogów nie są powielane w kodzie. Instrukcja dla
 * człowieka: `public/radio/README.md`.
 *
 * Zasady:
 *  - typ pozycji wynika z katalogu głównego (`music`, `idents`, ...),
 *  - podfoldery są skanowane rekursywnie i stają się tagami,
 *  - wyjątek: `music/vocal/` zmienia typ na `vocal` (wokal ma osobną regułę rotacji).
 */

import type { RadioItemType } from './radio.types';

export interface RadioFolder {
  /** Ścieżka względem `public/`. */
  dir: string;
  type: RadioItemType;
  /** Podfoldery pierwszego poziomu, które zmieniają typ pozycji. */
  subfolderTypes?: Readonly<Record<string, RadioItemType>>;
}

export const RADIO_FOLDERS = {
  music: { dir: 'radio/music', type: 'music', subfolderTypes: { vocal: 'vocal' } },
  idents: { dir: 'radio/idents', type: 'station_id' },
  jingles: { dir: 'radio/jingles', type: 'jingle' },
  interludes: { dir: 'radio/interludes', type: 'interlude' },
  timeAnnouncements: { dir: 'radio/time-announcements', type: 'time' },
} as const satisfies Record<string, RadioFolder>;

/** Tag nadawany przez `music/night/` - muzyka przeznaczona głównie na pasmo nocne. */
export const NIGHT_TAG = 'night';

export const RADIO_AUDIO_FILE = /\.(mp3|m4a|aac|ogg|oga|opus|flac|wav)$/i;

/** Zamienia nazwę na bezpieczny identyfikator: małe litery ASCII, cyfry i myślniki. */
export const slugify = (value: string): string =>
  value
    // „ł" nie rozkłada się w NFD, więc zamieniamy je ręcznie.
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Rozpoznaje typ i tagi pliku po jego ścieżce względem `public/`
 * (np. `radio/music/night/track03.mp3` → `music` + `["night"]`).
 * Zwraca `null` dla plików spoza katalogów radia i plików nie-audio.
 */
export function classifyRadioFile(
  relativePath: string
): { type: RadioItemType; tags: string[] } | null {
  const path = relativePath.replace(/\\/g, '/').replace(/^\/+/, '');
  if (!RADIO_AUDIO_FILE.test(path)) return null;

  const folder = (Object.values(RADIO_FOLDERS) as RadioFolder[]).find(({ dir }) =>
    path.startsWith(`${dir}/`)
  );
  if (!folder) return null;

  const subfolders = path
    .slice(folder.dir.length + 1)
    .split('/')
    .slice(0, -1);
  const tags = [...new Set(subfolders.map(slugify).filter(Boolean))];

  return { type: folder.subfolderTypes?.[tags[0]] ?? folder.type, tags };
}
